import { useState } from 'react'
import { nextTierAbove, type SelectableV2Plan } from './plan-select-cards'
import { CardOnFileCta, SubscriptionDialog, UpgradeAlternativeCta } from './subscription-dialog'
import { TopUpAmountPicker } from './top-up-amount-picker'
import type { TopUpKindSpec, TopUpSelection } from './top-up-kinds'

export interface TopUpPaywallProps {
  spec: TopUpKindSpec
  plan: SelectableV2Plan
  /** Whether the allowance this top-up refills is spent, which picks between the spec's two messages. */
  atLimit: boolean
  onPurchase: (selection: TopUpSelection) => void
  /** The secondary upgrade CTA renders only when given and a higher tier exists. */
  onUpgrade?: (tier: SelectableV2Plan) => void
  isPending?: boolean
  isUpgradePending?: boolean
  errorMessage?: string | null
  brandMark?: boolean
  className?: string
}

/**
 * Figma's "Subscription Dialog" for either top-up: tier-tinted wash, a slider
 * + stepper picking an amount, the card-on-file purchase CTA, and an upgrade
 * path for tiers with one above. Everything specific to credits or meeting hours
 * comes from the spec, so the two can't drift apart. Purely presentational;
 * the caller owns billing state and actions.
 */
export function TopUpPaywall({
  spec,
  plan,
  atLimit,
  onPurchase,
  onUpgrade,
  isPending,
  isUpgradePending,
  errorMessage,
  brandMark,
  className,
}: TopUpPaywallProps) {
  const [dollars, setDollars] = useState(() => spec.defaultDollars(plan))
  const nextTier = nextTierAbove(plan)
  // One purchase at a time: while either CTA's request is in flight, both
  // disable, or a user could start a top-up and a plan change together and be
  // charged for both.
  const busy = Boolean(isPending || isUpgradePending)

  return (
    <SubscriptionDialog
      plan={plan}
      title={spec.title(plan)}
      message={atLimit ? spec.limitMessage(plan) : spec.headroomMessage(plan)}
      brandMark={brandMark}
      className={className}
    >
      <TopUpAmountPicker
        plan={plan}
        dollars={dollars}
        onDollarsChange={setDollars}
        minDollars={spec.minDollars}
        maxDollars={spec.maxDollars}
        amountLabel={spec.amountLabel(dollars)}
      />

      <div className="flex flex-col gap-3">
        {errorMessage && <p className="text-red m-0 text-center text-[13px]">{errorMessage}</p>}

        <CardOnFileCta
          plan={plan}
          label={spec.ctaLabel}
          isPending={isPending}
          disabled={busy}
          onClick={() => onPurchase({ dollars, units: spec.unitsFor(dollars) })}
        />

        {nextTier && onUpgrade && (
          <UpgradeAlternativeCta
            targetTier={nextTier}
            isPending={isUpgradePending}
            disabled={busy}
            onUpgrade={onUpgrade}
          />
        )}
      </div>
    </SubscriptionDialog>
  )
}
